import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus, Plus, Save, X, Crown, Car } from 'lucide-react';
import { API } from '../../utils/apiConfig';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export function B2BMembersModal({ org, onClose, flash }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [createForm, setCreateForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    b2b_role: 'driver', job_title: '', is_driver: false,
    vehicle_make: '', vehicle_model: '', vehicle_color: '', vehicle_plate: '',
  });
  const [creating, setCreating] = useState(false);

  const inputStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.3)', background: 'rgba(15,23,42,0.6)', color: '#e2e8f0', fontSize: '13px' };
  const labelStyle = { fontSize: '11px', color: '#94a3b8', display: 'block', marginBottom: '3px', fontWeight: 600 };

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/b2b-orgs/${org.id}/members`, { headers: getHeaders() });
      setMembers(res.data.members || []);
    } catch { flash('error', t('errors.generic')); }
    finally { setLoading(false); }
  }, [org.id, flash, t]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!createForm.first_name || !createForm.email) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/b2b-orgs/create-member`, {
        ...createForm, org_id: org.org_id,
      }, { headers: getHeaders() });
      flash('success', t('b2b.memberCreated'));
      setShowCreate(false);
      setCreateForm({ first_name: '', last_name: '', email: '', phone: '', b2b_role: 'driver', job_title: '', is_driver: false, vehicle_make: '', vehicle_model: '', vehicle_color: '', vehicle_plate: '' });
      loadMembers();
    } catch (err) {
      flash('error', err.response?.data?.detail || t('errors.generic'));
    } finally { setCreating(false); }
  };

  const promoteToAdmin = async (member) => {
    try {
      await axios.post(`${API}/admin/b2b-orgs/assign-user`, {
        user_id: member.id, org_id: org.org_id, b2b_role: 'org_admin', job_title: member.job_title || '',
      }, { headers: getHeaders() });
      flash('success', `${member.firstName} ${member.lastName} → ${t('b2b.roles.org_admin')}`);
      loadMembers();
    } catch { flash('error', t('errors.generic')); }
  };

  const removeFromOrg = async (member) => {
    try {
      await axios.post(`${API}/admin/b2b-orgs/assign-user`, { user_id: member.id, org_id: '' }, { headers: getHeaders() });
      flash('success', t('b2b.removed'));
      loadMembers();
    } catch { flash('error', t('errors.generic')); }
  };

  return (
    <div data-testid="b2b-members-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: '#1e293b', borderRadius: '16px', maxWidth: '820px', width: '100%',
        maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
        border: '1px solid rgba(99,102,241,0.3)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(148,163,184,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={18} /> {t('b2b.viewMembers')}: {org.name}
            <span style={{ fontSize: '12px', background: 'rgba(99,102,241,0.2)', padding: '2px 8px', borderRadius: '10px', color: '#818cf8' }}>{members.length}</span>
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button data-testid="b2b-create-member-btn" onClick={() => { setShowCreate(!showCreate); setSelectedMember(null); }}
              style={{ background: showCreate ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)', border: `1px solid ${showCreate ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', color: showCreate ? '#ef4444' : '#22c55e', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
              {showCreate ? <><X size={14} /> {t('common.cancel')}</> : <><Plus size={14} /> {t('b2b.createMember')}</>}
            </button>
            <button data-testid="b2b-members-close" onClick={onClose}
              style={{ background: 'rgba(148,163,184,0.15)', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* Create Member Form */}
          {showCreate && (
            <form onSubmit={handleCreateMember} data-testid="b2b-create-member-form" style={{
              background: 'rgba(15,23,42,0.5)', borderRadius: '12px', padding: '18px', marginBottom: '20px',
              border: '1px solid rgba(34,197,94,0.2)',
            }}>
              <h4 style={{ margin: '0 0 14px', color: '#86efac', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <UserPlus size={16} /> {t('b2b.createMember')}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                <div>
                  <label style={labelStyle}>{t('b2b.firstName')} *</label>
                  <input data-testid="member-first-name" value={createForm.first_name} onChange={e => setCreateForm({ ...createForm, first_name: e.target.value })} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('b2b.lastName')}</label>
                  <input data-testid="member-last-name" value={createForm.last_name} onChange={e => setCreateForm({ ...createForm, last_name: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('b2b.email')} *</label>
                  <input data-testid="member-email" type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} required style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('b2b.phone')}</label>
                  <input data-testid="member-phone" value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="+49..." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t('b2b.role')}</label>
                  <select data-testid="member-role" value={createForm.b2b_role} onChange={e => setCreateForm({ ...createForm, b2b_role: e.target.value })} style={inputStyle}>
                    <option value="driver">{t('b2b.roles.driver')}</option>
                    <option value="org_admin">{t('b2b.roles.org_admin')}</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{t('b2b.jobTitle')}</label>
                  <input data-testid="member-job-title" value={createForm.job_title} onChange={e => setCreateForm({ ...createForm, job_title: e.target.value })} placeholder={t('b2b.jobTitlePlaceholder')} style={inputStyle} />
                </div>
              </div>

              {/* Driver Toggle */}
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: createForm.is_driver ? '#22c55e' : '#94a3b8' }}>
                  <input type="checkbox" data-testid="member-is-driver" checked={createForm.is_driver}
                    onChange={e => setCreateForm({ ...createForm, is_driver: e.target.checked })} />
                  <Car size={16} /> {t('b2b.isDriver')}
                </label>
              </div>

              {/* Vehicle Fields (conditional) */}
              {createForm.is_driver && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginTop: '12px', padding: '12px', background: 'rgba(34,197,94,0.05)', borderRadius: '8px', border: '1px solid rgba(34,197,94,0.15)' }}>
                  <div>
                    <label style={labelStyle}>{t('b2b.vehiclePlate')} *</label>
                    <input data-testid="member-vehicle-plate" value={createForm.vehicle_plate} onChange={e => setCreateForm({ ...createForm, vehicle_plate: e.target.value })} placeholder="HH-AB-1234" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('b2b.vehicleMake')}</label>
                    <input data-testid="member-vehicle-make" value={createForm.vehicle_make} onChange={e => setCreateForm({ ...createForm, vehicle_make: e.target.value })} placeholder="VW" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('b2b.vehicleModel')}</label>
                    <input data-testid="member-vehicle-model" value={createForm.vehicle_model} onChange={e => setCreateForm({ ...createForm, vehicle_model: e.target.value })} placeholder="Caddy" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('b2b.vehicleColor')}</label>
                    <input data-testid="member-vehicle-color" value={createForm.vehicle_color} onChange={e => setCreateForm({ ...createForm, vehicle_color: e.target.value })} placeholder="Weiss" style={inputStyle} />
                  </div>
                </div>
              )}

              <div style={{ marginTop: '14px' }}>
                <button type="submit" disabled={creating} data-testid="member-create-submit"
                  style={{ background: 'rgba(34,197,94,0.8)', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', color: '#fff', fontWeight: 600, fontSize: '13px', opacity: creating ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Save size={14} /> {creating ? '...' : t('b2b.createMember')}
                </button>
              </div>
            </form>
          )}

          {/* Member Detail View */}
          {selectedMember && (
            <div data-testid="b2b-member-detail" style={{
              background: 'rgba(15,23,42,0.5)', borderRadius: '12px', padding: '18px', marginBottom: '20px',
              border: '1px solid rgba(99,102,241,0.2)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div>
                  <h4 style={{ margin: 0, color: '#e2e8f0', fontSize: '16px' }}>{selectedMember.firstName} {selectedMember.lastName}</h4>
                  <p style={{ margin: '2px 0 0', color: '#94a3b8', fontSize: '13px' }}>{selectedMember.email}</p>
                </div>
                <button onClick={() => setSelectedMember(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={16} /></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                <div style={{ padding: '10px', background: 'rgba(99,102,241,0.08)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>{t('b2b.role')}</div>
                  <div style={{ fontSize: '13px', color: '#a5b4fc', fontWeight: 600 }}>{t(`b2b.roles.${selectedMember.b2b_role}`, selectedMember.b2b_role || 'driver')}</div>
                </div>
                <div style={{ padding: '10px', background: 'rgba(99,102,241,0.08)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>{t('b2b.jobTitle')}</div>
                  <div style={{ fontSize: '13px', color: '#e2e8f0' }}>{selectedMember.job_title || '—'}</div>
                </div>
                <div style={{ padding: '10px', background: 'rgba(99,102,241,0.08)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>{t('b2b.phone')}</div>
                  <div style={{ fontSize: '13px', color: '#e2e8f0' }}>{selectedMember.phone || '—'}</div>
                </div>
                <div style={{ padding: '10px', background: selectedMember.verification_provider === 'approved_by_org' ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.08)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>{t('b2b.status')}</div>
                  <div style={{ fontSize: '13px', color: selectedMember.identity_verified ? '#22c55e' : '#eab308', fontWeight: 600 }}>
                    {selectedMember.verification_provider === 'approved_by_org' ? t('b2b.verifiedByOrg') : selectedMember.identity_verified ? t('b2b.verified') : t('b2b.pending')}
                  </div>
                </div>
              </div>
              {/* Vehicle section */}
              {selectedMember.license_plate && (
                <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(34,197,94,0.05)', borderRadius: '8px', border: '1px solid rgba(34,197,94,0.15)' }}>
                  <div style={{ fontSize: '12px', color: '#86efac', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}><Car size={14} /> {t('b2b.vehicle')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>{t('b2b.vehiclePlate')}</span><br /><span style={{ color: '#e2e8f0', fontFamily: 'monospace', fontWeight: 600 }}>{selectedMember.license_plate}</span></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>{t('b2b.vehicleMake')}</span><br /><span style={{ color: '#e2e8f0' }}>{selectedMember.vehicle_make || '—'}</span></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>{t('b2b.vehicleModel')}</span><br /><span style={{ color: '#e2e8f0' }}>{selectedMember.vehicle_model || '—'}</span></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>{t('b2b.vehicleColor')}</span><br /><span style={{ color: '#e2e8f0' }}>{selectedMember.vehicle_color || '—'}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Member Table */}
          {loading ? <div className="spinner" /> : members.length === 0 ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>{t('b2b.noMembers')}</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{t('admin.users.name')}</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{t('b2b.email')}</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{t('b2b.role')}</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{t('b2b.jobTitle')}</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{t('b2b.vehicle')}</th>
                  <th style={{ textAlign: 'center', padding: '8px', color: '#94a3b8', fontSize: '12px' }}>{t('admin.users.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} data-testid={`b2b-member-${m.id}`}
                    onClick={() => { setSelectedMember(m); setShowCreate(false); }}
                    style={{ borderBottom: '1px solid rgba(148,163,184,0.1)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 8px', color: '#e2e8f0', fontWeight: 600 }}>{m.firstName} {m.lastName}</td>
                    <td style={{ padding: '10px 8px', color: '#94a3b8', fontSize: '13px' }}>{m.email}</td>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                        background: m.b2b_role === 'org_admin' ? 'rgba(234,179,8,0.15)' : 'rgba(99,102,241,0.15)',
                        color: m.b2b_role === 'org_admin' ? '#eab308' : '#a5b4fc',
                      }}>{t(`b2b.roles.${m.b2b_role}`, m.b2b_role || 'driver')}</span>
                    </td>
                    <td style={{ padding: '10px 8px', color: '#94a3b8', fontSize: '13px' }}>{m.job_title || '—'}</td>
                    <td style={{ padding: '10px 8px' }}>
                      {m.license_plate ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: 'rgba(34,197,94,0.1)', color: '#86efac' }}>
                          <Car size={12} /> {m.vehicle_make} {m.vehicle_model} <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{m.license_plate}</span>
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#475569' }}>{t('b2b.noVehicle')}</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        {m.b2b_role !== 'org_admin' && (
                          <button data-testid={`promote-${m.id}`} onClick={() => promoteToAdmin(m)}
                            title={t('b2b.promoteToAdmin')}
                            style={{ background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#eab308', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Crown size={11} /> Admin
                          </button>
                        )}
                        <button data-testid={`remove-member-${m.id}`} onClick={() => removeFromOrg(m)}
                          style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#ef4444', fontSize: '11px' }}>
                          <X size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
